import * as HighCharts from 'highcharts'
import React, { useEffect } from 'react'
import DarkUnica from 'highcharts/themes/dark-unica'
import ReactApexChart, { type Props } from 'react-apexcharts'
import i18Strings from '../../../i18n/en-US.json'
import { type kpiDailyState } from '../redux/kpiDailySlice'
import { type RootState } from '../../../store'
import { connect, useDispatch } from 'react-redux'
import { fetchKpiDailyData } from '../redux/actionCreators'
import { Card, CardContent, Grid } from '@mui/material'
import { useCircleGaugeParams } from '../../../customHooks/circleGaugeDim'
import { useNavigate } from 'react-router-dom'
import { changeKpiType } from '../redux/changeKpiType'

const dataCompletenessStrings = i18Strings.AMIDataCollectionStrings.dataCompleteness

// Loop over a list of KPI IDs, loop and pull the values from the list of DailyKpi data
// and produce an array of their values for passing to the series data of the chart
function createSeriesData (): number[] {
  const kpiValues: number[] = []
  // eslint-disable-next-line no-unreachable-loop
  for (let i = 2; i < 6; i++) {
    kpiValues.push(i * i * 5)
  }
  return kpiValues
}

function showLegend (reportData: any): any {
  const nr = reportData.series.length
  const ret = []

  if (nr === 1) {
    ret.push(
        <div style={{ borderLeft: '10px', borderLeftStyle: 'solid', borderLeftColor: reportData.colors[0], margin: '10px', padding: '10px' }}>{reportData.description}</div>
    )
    return ret
  }
  for (let i = 0; i < nr; i++) {
    const color = reportData.colors[i]
    const desc = reportData.description[i]
    const val = reportData.series[i]
    ret.push(
        <div key={i} style={{ borderLeft: '10px', borderLeftStyle: 'solid', borderLeftColor: color, margin: '10px', padding: '10px' }}>{desc} <big><strong style={{ marginLeft: '5px', color }}>{val} %</strong></big></div>
    )
  }
  return ret
}

function getReactApexChartRadialBar (reportData: any, height: string, centerFont: string): any {
  // calculate the center average
  const chartOptions: Props = {
    series: reportData.series,
    options: {
      chart: {
        type: 'radialBar',
        background: 'transparent'
      },
      theme: { mode: 'dark' },
      colors: reportData.colors,
      stroke: {
        // This puts a little circle in the chart when it's zero so the
        // chart doesn't look like it's missing data.
        lineCap: 'butt'
      },
      fill: {
        // If there is more series data than colors it repeats
        colors: reportData.colors
      },
      grid: {
        padding: {
          left: 5,
          right: 5
        }
      },
      plotOptions: {
        radialBar: {
          dataLabels: {
            name: {
              show: false
            },
            value: {
              show: true,
              fontSize: centerFont,
              fontWeight: 200,
              color: 'white'
            },
            total: {
              show: reportData.series.length === 0,
              label: dataCompletenessStrings.total,
              fontSize: centerFont,
              formatter: function (e) {
                // All of this because the default center avgs the series data
                // but produces a string tha doesn't fit on the screen for cases
                // like 66.3333333 etc.
                // The documentation leaves a lot to be desired. I set a breakpoint to
                // capture the event. I'm assuming here that the config is always present.
                const seriesData = e.config.series
                if (seriesData.length === 0) {
                  return 'No Data'
                } else {
                  const avg = ((seriesData.reduce((partialSum: number, a: number) => partialSum + a, 0)) / seriesData.length)
                  return avg.toFixed(2) + ' %'
                }
              }
            }
          },
          hollow: {
            margin: 15,
            size: reportData.series.length === 1 ? '50%' : '35%'
          },
          track: {
            background: '#202020AA',
            strokeWidth: '100px',
            show: true
          }
        }
      }
    }
  }

  return (
      <>
        <Grid>
          <div style={{ display: 'flex', alignItems: 'center', height: '100%', fontSize: '12px', lineHeight: '1em' }}>
            <div className="centerChartX" style={{ height: '100%', width: '100%' }}>
              <ReactApexChart
                  options={chartOptions.options}
                  series={chartOptions.series}
                  type='radialBar' height={height}
              />
            </div>
            <Grid style={{ width: '60%' }}>
              {showLegend(reportData)}
            </Grid>
          </div>
        </Grid>
      </>
  )
}
function getReactApexChartPie (dailyKpis: kpiDailyState, height: string): any {
  const seriesData = createSeriesData()

  // calculate the center average
  const chartOptions: Props = {
    series: seriesData,
    options: {
      chart: {
        width: 380,
        type: 'pie'
      },
      labels: ['Team A', 'Team B', 'Team C', 'Team D', 'Team E'],
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 200
          },
          legend: {
            position: 'bottom'
          }
        }
      }]
    }
  }

  return (
      <ReactApexChart
          options={chartOptions.options}
          series={chartOptions.series}
          type='pie' height={height}
      />
  )
}
function getReactApexChartBar (reportData: any, isHistory: boolean, height: string): any {
  let categories = reportData.categories
  if (isHistory) {
    if ((reportData.history.categories != null)) {
      console.log('use real', reportData.history)
      categories = reportData.history.categories
    } else {
      console.log('generate categories', reportData.history.series[0].data.length)
      categories = generateHistoricalDates(reportData.history.series[0].data.length)
    }
  }
  console.log('getReactApexChartBar', categories)
  const chartOptions: Props = {
    series: isHistory ? reportData.history.series : reportData.series,
    options: {
      chart: {
        height: 300,
        type: 'bar',
        background: 'transparent'
      },
      theme: { mode: 'dark' },
      colors: reportData.colors,
      plotOptions: {
        bar: {
          borderRadius: 2,
          columnWidth: '45%',
          dataLabels: {
            position: 'center' // top, center, bottom
          }
        }
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['#313031']
      },
      dataLabels: {
        enabled: false,
        offsetY: -20,
        style: {
          fontSize: '12px',
          colors: ['#fff']
        }
      },
      xaxis: {
        categories,
        position: 'bottom',
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: true
        },
        crosshairs: {
          fill: {
            type: 'gradient',
            gradient: {
              colorFrom: '#D8E3F0',
              colorTo: '#BED1E6',
              stops: [0, 100],
              opacityFrom: 0.4,
              opacityTo: 0.5
            }
          }
        },
        tooltip: {
          enabled: false
        },
        labels: {
          show: true,
          style: {
            colors: '#979798'
          }
        }
      },
      yaxis: {
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        },
        labels: {
          show: true,
          style: {
            colors: '#bbbbbc'
          },
          formatter: function (val) {
            const s: string = reportData.suffix
            return `${val} ${s}`
          }
        }
      }
    }
  }

  return (
      <ReactApexChart
          options={chartOptions.options}
          series={chartOptions.series}
          type='bar' height={height}
      />
  )
}
function getReactApexChartLine (dailyKpis: kpiDailyState, height: string): any {
  // calculate the center average
  const chartOptions: Props = {
    options: {
      chart: {
        height: 350,
        type: 'line',
        zoom: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'straight'
      },
      title: {
        text: 'Product Trends by Month',
        align: 'left'
      },
      grid: {
        row: {
          colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
          opacity: 0.5
        }
      },
      xaxis: {
        categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep']
      }
    }
  }

  return (
      <ReactApexChart
          options={chartOptions.options}
          series={chartOptions.series}
          type='line' height={height}
      />
  )
}

function generateHistoricalDates (nr: number): string[] {
  const items = []
  for (let i = 0; i < nr; i++) {
    const date = new Date()
    date.setDate(date.getDate() - i)
    const formattedDate = date.toLocaleDateString('en-GB')
    items.push(formattedDate)
  }
  return items.reverse()
}

function doReport (reportData: any, history: boolean, height: string, centerFont: string): any {
  if (history) {
    return getReactApexChartBar(reportData, true, height)
  }
  if (reportData.type === 'radialBar') {
    return getReactApexChartRadialBar(reportData, height, centerFont)
  } else if (reportData.type === 'bar') {
    return getReactApexChartBar(reportData, false, height)
  } else if (reportData.type === 'line') {
    return getReactApexChartLine(reportData, height)
  } else {
    return getReactApexChartPie(reportData, height)
  }
}

const DemoKpiItem = (props: any): any => {
  DarkUnica(HighCharts)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [height, centerFont] = useCircleGaugeParams()

  useEffect(() => {
    // Load the metrics needed for the gauges
    fetchKpis()
  }, [])

  const fetchKpis = (): void => {
    // props.fetchKpiDailyData(auth.user?.profile.customer as string)

  }

  if (props.reportData == null) {
    return
  }

  const handleOnclick = (title: string): any => {
    if (window.location.pathname !== '/kpi-history' && props.reportData.type !== 'radialBar') {
      dispatch(changeKpiType(title))
      navigate('/kpi-history')
    }
  }

  console.log('DemoKpiItem', props.reportData)

  return (
    <Card onClick={() => { handleOnclick(props.reportData.name) }} className='kpi-card' sx={{ height: '100%', borderRadius: '10px', backgroundColor: '#313030', color: '#f0f0f0' }}>
      <div style={{ fontSize: '16px', padding: '15px', letterSpacing: '1px', textTransform: 'uppercase' }}>{props.reportData.name}{props.history === true ? ' History' : ''}</div>
      <CardContent>
        <Grid>
          <div id='data-comp-container' className='centerChart'>
            <div className='circleChartDataComp'>
              {doReport(props.reportData, props.history, height, centerFont)}
            </div>
          </div>
        </Grid>
      </CardContent>
    </Card>
  )
}

interface DispatchToProps {
  fetchKpiDailyData: (tenant: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchKpiDailyData: (tenant: string) => dispatch(fetchKpiDailyData(tenant))
})

interface StateToProps {
  kpiDaily: kpiDailyState
  activeTenant: string
}

const mapStateToProps = (state: RootState): StateToProps => ({
  kpiDaily: state.kpiDailyData,
  activeTenant: state.tenant
})

const connector = connect(mapStateToProps, mapDispatchToProps)
// type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(DemoKpiItem)
